<div class="space-y-6">
  <app-page-header
    title="Mon profil"
    subtitle="Tes informations personnelles, identifiants de connexion et permissions sur la plateforme."
  />

  @if (user(); as u) {
    <!-- Header card : avatar + name + email + roles -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-4">
          @if (u.avatarUrl) {
            <img [src]="u.avatarUrl" [alt]="displayName()" class="h-20 w-20 rounded-full object-cover ring-2 ring-brand-100 dark:ring-brand-900/40" />
          } @else {
            <span class="flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 text-xl font-semibold text-brand-700 ring-2 ring-brand-100 dark:bg-brand-900/30 dark:text-brand-300 dark:ring-brand-900/40">
              {{ userInitials() }}
            </span>
          }
          <div class="min-w-0 space-y-1">
            <h2 class="text-xl font-semibold text-gray-800 dark:text-white/90">{{ displayName() }}</h2>
            @if (u.email) {
              <p class="truncate text-sm text-gray-500 dark:text-gray-400">{{ u.email }}</p>
            }
            @if (businessRoles().length > 0) {
              <div class="flex flex-wrap gap-1.5 pt-1">
                @for (r of businessRoles(); track r) {
                  <span class="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ r }}</span>
                }
              </div>
            }
          </div>
        </div>
        @if (u.active === false) {
          <span class="inline-flex h-fit items-center rounded-full bg-error-50 px-3 py-1 text-xs font-medium text-error-700 dark:bg-error-500/10 dark:text-error-400">
            Compte désactivé
          </span>
        }
      </div>
    </section>

    <div class="grid gap-6 lg:grid-cols-2">
      <!-- Personal info -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Informations personnelles</h3>
          <button
            type="button"
            (click)="openEdit()"
            class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Modifier
          </button>
        </div>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Prénom</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ u.firstName || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Nom</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ u.lastName || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Identifiant</dt>
            <dd class="text-right font-mono text-xs text-gray-700 dark:text-gray-300">{{ u.username || '—' }}</dd>
          </div>
          @if (u.address) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">Adresse</dt>
              <dd class="text-right text-gray-800 dark:text-white/90">{{ u.address }}</dd>
            </div>
          }
        </dl>
      </section>

      <!-- Account credentials (email + phone + password) -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Identifiants & sécurité</h3>

        <div class="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
          <!-- Email -->
          <div class="flex items-center justify-between gap-4 py-3">
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Email</p>
              <p class="mt-0.5 truncate text-sm text-gray-800 dark:text-white/90">{{ u.email || '—' }}</p>
            </div>
            <button
              type="button"
              (click)="openEmail()"
              class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              Changer
            </button>
          </div>

          <!-- Phone -->
          <div class="flex items-center justify-between gap-4 py-3">
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Téléphone</p>
              <p class="mt-0.5 truncate font-mono text-sm text-gray-800 dark:text-white/90">{{ u.phone || '—' }}</p>
            </div>
            <button
              type="button"
              (click)="openPhone()"
              class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              Changer
            </button>
          </div>

          <!-- Password -->
          <div class="flex items-center justify-between gap-4 py-3">
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Mot de passe</p>
              <p class="mt-0.5 text-sm text-gray-800 dark:text-white/90">••••••••••</p>
              @if (u.changePassword) {
                <p class="mt-0.5 text-xs text-warning-600 dark:text-warning-400">Changement requis à la prochaine connexion.</p>
              }
            </div>
            <button
              type="button"
              (click)="openPassword()"
              class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              Changer
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Roles & permissions (read-only) -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Rôles & permissions</h3>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Géré par un administrateur. Demande à un admin pour toute modification.</p>

      <div class="mt-4 space-y-4">
        <div>
          <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Rôles ({{ businessRoles().length }})
          </p>
          @if (businessRoles().length === 0) {
            <p class="text-sm text-gray-400">Aucun rôle métier assigné.</p>
          } @else {
            <div class="flex flex-wrap gap-1.5">
              @for (r of businessRoles(); track r) {
                <span class="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ r }}</span>
              }
            </div>
          }
        </div>
        <div>
          <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Permissions fines ({{ permissions().length }})
          </p>
          @if (permissions().length === 0) {
            <p class="text-sm text-gray-400">Aucune permission spécifique au-delà des rôles.</p>
          } @else {
            <div class="flex flex-wrap gap-1">
              @for (p of permissions(); track p) {
                <span class="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ p }}</span>
              }
            </div>
          }
        </div>
      </div>
    </section>
  } @else {
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <p class="text-sm text-gray-500 dark:text-gray-400">Chargement du profil…</p>
    </section>
  }
</div>

<!-- ─────────────────────────────────────────────────────────────────────
     Personal info modal
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="editOpen()" (close)="closeEdit()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Modifier mes informations</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Prénom et nom. Pour l'email ou le téléphone, utilise les actions dédiées (vérification OTP).</p>

  @if (editError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ editError() }}
    </div>
  }

  <form [formGroup]="editForm" (ngSubmit)="submitEdit()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Prénom</label>
      <input
        type="text"
        formControlName="firstName"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nom</label>
      <input
        type="text"
        formControlName="lastName"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeEdit()" [disabled]="editSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="editSaving() || editForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (editSaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>

<!-- ─────────────────────────────────────────────────────────────────────
     Password modal
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="passwordOpen()" (close)="closePassword()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Changer mon mot de passe</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Choisis un mot de passe d'au moins 8 caractères, différent du précédent.</p>

  @if (passwordError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ passwordError() }}
    </div>
  }
  @if (passwordOk()) {
    <div class="mt-4 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-700 dark:border-brand-800 dark:bg-brand-500/10 dark:text-brand-400">
      Mot de passe modifié avec succès.
    </div>
  }

  <form [formGroup]="passwordForm" (ngSubmit)="submitPassword()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Mot de passe actuel</label>
      <input
        type="password"
        formControlName="oldPassword"
        autocomplete="current-password"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nouveau mot de passe</label>
      <input
        type="password"
        formControlName="newPassword"
        autocomplete="new-password"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
      @if (passwordForm.controls.newPassword.touched && passwordForm.controls.newPassword.hasError('minlength')) {
        <p class="mt-1 text-xs text-error-600 dark:text-error-400">Minimum 8 caractères.</p>
      }
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Confirmer le mot de passe</label>
      <input
        type="password"
        formControlName="newPasswordRepeat"
        autocomplete="new-password"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
      @if (passwordForm.controls.newPasswordRepeat.touched && passwordForm.controls.newPasswordRepeat.hasError('passwordMismatch')) {
        <p class="mt-1 text-xs text-error-600 dark:text-error-400">Les mots de passe ne correspondent pas.</p>
      }
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closePassword()" [disabled]="passwordSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Fermer</button>
      <button type="submit" [disabled]="passwordSaving() || passwordForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (passwordSaving()) { Modification… } @else { Modifier }
      </button>
    </div>
  </form>
</app-modal>

<!-- ─────────────────────────────────────────────────────────────────────
     Email modal (2-step OTP)
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="emailOpen()" (close)="closeEmail()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Changer mon email</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    @if (emailStep() === 'request') {
      Renseigne ta nouvelle adresse. Un code de vérification y sera envoyé.
    } @else {
      Entre le code à 6 chiffres reçu sur <strong>{{ emailForm.controls.email.value }}</strong>.
    }
  </p>

  @if (emailError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ emailError() }}
    </div>
  }

  <form [formGroup]="emailForm" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nouvel email</label>
      <input
        type="email"
        formControlName="email"
        [readonly]="emailStep() === 'verify'"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 disabled:opacity-60 read-only:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:read-only:bg-gray-800/60"
      />
    </div>

    @if (emailStep() === 'verify') {
      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Code OTP (6 chiffres)</label>
        <input
          type="text"
          inputmode="numeric"
          maxlength="6"
          formControlName="otp"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-center font-mono text-lg tracking-widest text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        />
      </div>
    }

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeEmail()" [disabled]="emailSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      @if (emailStep() === 'request') {
        <button type="button" (click)="submitEmailRequest()" [disabled]="emailSaving() || emailForm.controls.email.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (emailSaving()) { Envoi… } @else { Envoyer le code }
        </button>
      } @else {
        <button type="button" (click)="submitEmailVerify()" [disabled]="emailSaving() || emailForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (emailSaving()) { Vérification… } @else { Confirmer }
        </button>
      }
    </div>
  </form>
</app-modal>

<!-- ─────────────────────────────────────────────────────────────────────
     Phone modal (2-step OTP)
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="phoneOpen()" (close)="closePhone()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Changer mon téléphone</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    @if (phoneStep() === 'request') {
      Renseigne ton nouveau numéro. Un code de vérification y sera envoyé.
    } @else {
      Entre le code à 6 chiffres reçu au <strong>{{ phoneForm.controls.phone.value }}</strong>.
    }
  </p>

  @if (phoneError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ phoneError() }}
    </div>
  }

  <form [formGroup]="phoneForm" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nouveau téléphone</label>
      <input
        type="tel"
        formControlName="phone"
        [readonly]="phoneStep() === 'verify'"
        placeholder="+221 77 123 45 67"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 read-only:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:read-only:bg-gray-800/60"
      />
    </div>

    @if (phoneStep() === 'verify') {
      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Code OTP (6 chiffres)</label>
        <input
          type="text"
          inputmode="numeric"
          maxlength="6"
          formControlName="otp"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-center font-mono text-lg tracking-widest text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        />
      </div>
    }

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closePhone()" [disabled]="phoneSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      @if (phoneStep() === 'request') {
        <button type="button" (click)="submitPhoneRequest()" [disabled]="phoneSaving() || phoneForm.controls.phone.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (phoneSaving()) { Envoi… } @else { Envoyer le code }
        </button>
      } @else {
        <button type="button" (click)="submitPhoneVerify()" [disabled]="phoneSaving() || phoneForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (phoneSaving()) { Vérification… } @else { Confirmer }
        </button>
      }
    </div>
  </form>
</app-modal>
